import { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import GpsFixedIcon from '@mui/icons-material/GpsFixed';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import { toast } from 'react-toastify';
import {
  useSniperGroups,
  useDeleteSniperGroup,
  useUpsertSniperEvent,
  usePanicAll,
  useClearGlobalHalt,
} from '../api/useSniper';
import { SniperEventCard } from '../components/sniper/SniperEventCard';
import { SniperGroupsLanding } from '../components/sniper/SniperGroupsLanding';
import { SniperGroupControls } from '../components/sniper/SniperGroupControls';
import { SniperCreateGroupDialog } from '../components/sniper/SniperCreateGroupDialog';
import { anySniperHalt } from '../utils/sniperGroupState';
import type { SniperGroup } from '../types/sniper.types';

/**
 * The Sniper page: a groups LANDING (list of groups, each with a state badge +
 * per-group Start(test|normal)/Stop/Panic), plus a GLOBAL panic + halted
 * indicator in the header. Clicking a group opens its DETAIL — the
 * Group→Event→Market view (live state line + ARMED badges + per-market draft
 * params + enabled toggle). Firing is gated by the group being Started; there
 * is no single master switch. Its own page (`/predictions/sniper`).
 */
export function SniperPage() {
  const { data: groups = [], isLoading, isError } = useSniperGroups();
  const deleteGroup = useDeleteSniperGroup();
  const upsertEvent = useUpsertSniperEvent();
  const panicAll = usePanicAll();
  const clearGlobalHalt = useClearGlobalHalt();

  const [selectedGroupId, setSelectedGroupId] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [confirmPanicAll, setConfirmPanicAll] = useState(false);

  const selectedGroup = useMemo(
    () => groups.find((g) => g._id === selectedGroupId) ?? null,
    [groups, selectedGroupId],
  );

  const halted = anySniperHalt(groups);

  // ── add-event form (scoped to the selected group) ─────────────────────────
  const [newEvent, setNewEvent] = useState({
    fixtureId: '',
    homeTeam: '',
    awayTeam: '',
    league: '',
  });
  const newFixtureIdNum = Number(newEvent.fixtureId);
  const canAddEvent =
    !!selectedGroup &&
    Number.isInteger(newFixtureIdNum) &&
    newFixtureIdNum > 0 &&
    newEvent.homeTeam.trim().length > 0 &&
    newEvent.awayTeam.trim().length > 0;

  const addEvent = () => {
    if (!canAddEvent || !selectedGroup) return;
    upsertEvent.mutate(
      {
        groupId: selectedGroup._id,
        dto: {
          fixtureId: newFixtureIdNum,
          homeTeam: newEvent.homeTeam.trim(),
          awayTeam: newEvent.awayTeam.trim(),
          league: newEvent.league.trim() || null,
        },
      },
      {
        onSuccess: () =>
          setNewEvent({ fixtureId: '', homeTeam: '', awayTeam: '', league: '' }),
        onError: () => {
          toast.error('Failed to add event.');
        },
      },
    );
  };

  const removeGroup = (group: SniperGroup) => {
    if (
      !window.confirm(
        `Delete group “${group.name}” and ALL its markets? This cannot be undone.`,
      )
    ) {
      return;
    }
    deleteGroup.mutate(group._id, {
      onSuccess: () => {
        if (group._id === selectedGroupId) setSelectedGroupId(null);
      },
      onError: () => {
        toast.error('Failed to delete group.');
      },
    });
  };

  const confirmPanicAllRun = () => {
    setConfirmPanicAll(false);
    panicAll.mutate(undefined, {
      onError: () => {
        toast.error('Failed to panic all groups.');
      },
    });
  };

  return (
    <Box
      sx={{
        px: 2,
        pt: 1,
        // The app shell (Layout.tsx) is a 100vh grid with NO top bar, so the
        // content track is the full viewport height — fill it (like
        // NotificationsPage). `calc(100vh - 64px)` left a 64px gap at the bottom.
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        spacing={1.5}
        sx={{ mb: 1, flexShrink: 0 }}
      >
        <GpsFixedIcon sx={{ color: halted ? 'error.main' : 'text.secondary' }} />
        <Typography variant="h5" sx={{ color: 'common.white' }}>
          Sniper
        </Typography>
        {halted && (
          <Chip
            size="small"
            color="error"
            label="HALTED"
            sx={{ fontWeight: 800 }}
          />
        )}
        <Box sx={{ flex: 1 }} />
        {!selectedGroup && (
          <Button
            variant="contained"
            size="small"
            startIcon={<AddIcon />}
            onClick={() => setCreateOpen(true)}
            sx={{ fontSize: '0.7rem' }}
          >
            New group
          </Button>
        )}
        {halted && (
          <Tooltip title="Clear the global halt on ALL groups. Does NOT resume — Start each group after.">
            <span>
              <Button
                variant="outlined"
                color="error"
                size="small"
                startIcon={
                  clearGlobalHalt.isLoading ? (
                    <CircularProgress size={14} color="inherit" />
                  ) : (
                    <LockOpenIcon />
                  )
                }
                onClick={() =>
                  clearGlobalHalt.mutate(undefined, {
                    onError: () => {
                      toast.error('Failed to clear global halt.');
                    },
                  })
                }
                disabled={clearGlobalHalt.isLoading}
                sx={{ fontSize: '0.7rem' }}
              >
                Clear halt (all)
              </Button>
            </span>
          </Tooltip>
        )}
        <Tooltip title="Immediately PANIC every Sniper group + cancel all orders">
          <span>
            <Button
              variant="contained"
              color="error"
              size="small"
              startIcon={
                panicAll.isLoading ? (
                  <CircularProgress size={14} color="inherit" />
                ) : (
                  <WarningAmberIcon />
                )
              }
              onClick={() => setConfirmPanicAll(true)}
              disabled={panicAll.isLoading}
              sx={{ fontSize: '0.7rem' }}
            >
              PANIC ALL
            </Button>
          </span>
        </Tooltip>
      </Stack>

      {halted && (
        <Alert severity="error" variant="outlined" sx={{ mb: 2 }}>
          One or more Sniper groups are HALTED. Clearing a halt does NOT resume —
          a halted group stays stopped until it is Started again.
        </Alert>
      )}

      <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
        {isError && (
          <Alert severity="error" sx={{ mb: 1 }}>
            Failed to load Sniper groups.
          </Alert>
        )}

        {!selectedGroup ? (
          // ── LANDING: list of groups (create via the header "New group") ───
          <SniperGroupsLanding
            groups={groups}
            loading={isLoading}
            onSelectGroup={setSelectedGroupId}
            onDeleteGroup={removeGroup}
            onCreateGroup={() => setCreateOpen(true)}
            deleteBusy={deleteGroup.isLoading}
          />
        ) : (
          // ── DETAIL: selected group → events → markets ─────────────────────
          <>
            <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
              <Stack spacing={2}>
                <Stack
                  direction="row"
                  spacing={1.5}
                  alignItems="center"
                  flexWrap="wrap"
                >
                  <Button
                    size="small"
                    startIcon={<ArrowBackIcon />}
                    onClick={() => setSelectedGroupId(null)}
                  >
                    All groups
                  </Button>
                  <Typography
                    variant="h6"
                    sx={{ color: 'common.white', fontWeight: 600 }}
                  >
                    {selectedGroup.name}
                  </Typography>
                  <Box sx={{ flex: 1 }} />
                  <SniperGroupControls group={selectedGroup} />
                </Stack>

                <Divider flexItem />

                {/* Add an event (match) by LSports fixture ID. */}
                <Stack
                  direction={{ xs: 'column', md: 'row' }}
                  spacing={1.5}
                  alignItems={{ md: 'center' }}
                >
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ minWidth: 80 }}
                  >
                    Add match
                  </Typography>
                  <TextField
                    size="small"
                    label="Fixture ID"
                    value={newEvent.fixtureId}
                    onChange={(e) =>
                      setNewEvent((m) => ({
                        ...m,
                        fixtureId: e.target.value.replace(/[^0-9]/g, ''),
                      }))
                    }
                    inputProps={{ inputMode: 'numeric' }}
                    sx={{ width: 120 }}
                  />
                  <TextField
                    size="small"
                    label="Home team"
                    value={newEvent.homeTeam}
                    onChange={(e) =>
                      setNewEvent((m) => ({ ...m, homeTeam: e.target.value }))
                    }
                    sx={{ flex: 1, minWidth: 140 }}
                  />
                  <TextField
                    size="small"
                    label="Away team"
                    value={newEvent.awayTeam}
                    onChange={(e) =>
                      setNewEvent((m) => ({ ...m, awayTeam: e.target.value }))
                    }
                    sx={{ flex: 1, minWidth: 140 }}
                  />
                  <TextField
                    size="small"
                    label="League (optional)"
                    value={newEvent.league}
                    onChange={(e) =>
                      setNewEvent((m) => ({ ...m, league: e.target.value }))
                    }
                    sx={{ flex: 1, minWidth: 130 }}
                  />
                  <Button
                    variant="contained"
                    size="small"
                    onClick={addEvent}
                    disabled={!canAddEvent || upsertEvent.isLoading}
                  >
                    {upsertEvent.isLoading ? 'Adding…' : 'Add'}
                  </Button>
                </Stack>
              </Stack>
            </Paper>

            {(selectedGroup.events?.length ?? 0) === 0 ? (
              <Box sx={{ textAlign: 'center', py: 6 }}>
                <Typography sx={{ color: 'grey.500', fontSize: '0.85rem' }}>
                  No matches in this group — add one above.
                </Typography>
              </Box>
            ) : (
              <Stack spacing={2}>
                {(selectedGroup.events ?? []).map((event) => (
                  <SniperEventCard key={event.fixtureId} event={event} />
                ))}
              </Stack>
            )}
          </>
        )}
      </Box>

      <SniperCreateGroupDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
      />

      <Dialog open={confirmPanicAll} onClose={() => setConfirmPanicAll(false)}>
        <DialogTitle>PANIC every Sniper group?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This immediately halts EVERY Sniper group and cancels all their
            orders. Groups stay halted until you clear the halt AND Start them
            again. Continue?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmPanicAll(false)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={confirmPanicAllRun}>
            PANIC ALL
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
